import type { ExpressTier } from "data-types";
import { getSecondsUntilCutoff } from "../../../ChooseTreatmentVariants/FreeDosageChooseTreatmentV2/components/expressDeliveryUtils";
import type { ExpressUpsellVariant } from "../components/ExpressDeliveryUpsellPopup";
import { UPSELL_CONFIGS } from "./expressUpsellConfig";

export const SHIPPING_EVENTS = {
  TOGGLE_CLICK: "Funnel Express Delivery Toggle Click",
  ELIGIBLE_CHECK: "Funnel Delivery Eligible Check",
  UPSELL_POPUP: "Funnel Delivery Upsell Popup",
  UPSELL_APPLY: "Funnel Delivery Upsell Popup Apply",
  EXPRESS_AB_TEST: "Express Delivery Test",
} as const;

export type CartLike = {
  selected_service_option?: string | null;
  selected_express_tier?: string | null;
  shipping_company_code?: string | null;
  shipping_fee?: number | null;
  custom_shipping_fee?: number | null;
};

export type DeliveryMethodLabel =
  | "Same day delivery" | "Next day delivery" | "Uber" | "DHL" | "Self-pickup";

export const resolveDeliveryMethodLabel = (cart: CartLike): DeliveryMethodLabel => {
  if (cart.selected_service_option === "self_pickup") return "Self-pickup";
  if (cart.selected_service_option === "express") {
    if (cart.selected_express_tier === "same_day") return "Same day delivery";
    if (cart.selected_express_tier === "next_day") return "Next day delivery";
    return "Uber"; // uber_2h tier or uber company
  }
  return "DHL";
};

// "what the user chose on the previous screen": express uses custom_shipping_fee,
// regular/DHL uses shipping_fee (0 when free, the configured fee — 4.9 — when below the
// free-shipping grams threshold). Mirrors ShippingPageProvider's "Shipping Fee" logic.
export const resolveDeliveryPriceEuros = (cart: CartLike): number => {
  const isExpress = cart.selected_service_option === "express";
  const fee = isExpress ? cart.custom_shipping_fee : cart.shipping_fee;
  return Number(fee ?? 0);
};

export const resolveCutoffTimerShown = (tiers: ExpressTier[]): boolean => {
  const sameDay = tiers.find(t => t.tier === "same_day");
  if (!sameDay || !sameDay.cutoffTime || sameDay.isCutoffPassed) return false;
  return getSecondsUntilCutoff(sameDay.cutoffTime) !== null;
};

const isTierEligible = (tiers: ExpressTier[], tierName: string): boolean =>
  tiers.some(t => t.tier === tierName && t.isAvailable && t.isCutoffPassed !== true);

// "Before cut off time" (spec §2): a same_day tier exists and its cutoff has not passed.
// Distinct from "Cut off timer show" — this is the boolean state, the timer is the countdown.
const isBeforeSameDayCutoff = (tiers: ExpressTier[]): boolean =>
  tiers.some(t => t.tier === "same_day" && t.isCutoffPassed === false);

export const buildToggleClickProps = (args: { addressSaved: boolean }) => ({
  "Address saved": args.addressSaved ? "Yes" : "No",
});

export const buildEligibleCheckProps = (args: {
  tiers: ExpressTier[];
  zip: string;
  availablePharmacies: number;
}) => ({
  "Uber eligible": isTierEligible(args.tiers, "uber_2h") ? "Yes" : "No",
  "Same day delivery eligible": isTierEligible(args.tiers, "same_day") ? "Yes" : "No",
  "Next day delivery eligible": isTierEligible(args.tiers, "next_day") ? "Yes" : "No",
  "Zip code": args.zip,
  "Before cut off time": isBeforeSameDayCutoff(args.tiers) ? "Yes" : "No",
  "Available pharmacies": args.availablePharmacies,
  "Cut off timer show": resolveCutoffTimerShown(args.tiers) ? "Yes" : "No",
});

export const resolveUpsellOfferLabel = (variant: ExpressUpsellVariant): string => {
  if (variant === "same_day") return "Same day delivery";
  if (variant === "next_day") return "Next day delivery";
  return "Uber";
};

// "Upsell price" (spec §4): the ADDITION to the current delivery price, not the offer's
// absolute price. = offer price (UPSELL_CONFIGS, cents→euros) − current delivery price.
export const resolveUpsellPriceAddition = (cart: CartLike, variant: ExpressUpsellVariant): number => {
  const offerEuros = UPSELL_CONFIGS[variant].priceCents / 100;
  const currentEuros = resolveDeliveryPriceEuros(cart);
  // Floor at 0 (a negative "addition" pollutes funnel sums) and round to 2dp (kills float
  // noise: 5.9 - 4.9 = 0.8999999999999995).
  return Math.max(0, Math.round((offerEuros - currentEuros) * 100) / 100);
};

export const buildUpsellPopupProps = (args: { cart: CartLike; variant: ExpressUpsellVariant }) => ({
  "Current delivery": resolveDeliveryMethodLabel(args.cart),
  "Upsell popup type offer": resolveUpsellOfferLabel(args.variant),
  "Upsell price": resolveUpsellPriceAddition(args.cart, args.variant),
});

// Withholding the catalog auto-discount from returning customers.
export const ABTEST_AUTO_CATALOG_COUPON_EVENT = "funnel continue shipping for A/B test";

// Only participants carry the property — omitted entirely for FTB / non-discounted carts.
export const buildAbtestAutoCatalogCouponProps = (variant?: string | null) =>
  variant ? { "auto coupon test": variant } : {};

export const buildAbtestAutoCatalogCouponContinueProps = (variant: string, eligibleForCoupon: boolean) => ({
  "auto coupon test": variant,
  "eligible for coupon": eligibleForCoupon ? "yes" : "no",
});
